import { EnvironmentOutlined, EyeOutlined } from '@ant-design/icons'
import { Flex, Modal, Row, Select, Table } from 'antd'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  getBannerCategoryList,
  getBannerClickCount,
  getBannerList,
} from '../../api/banner.api'
import ContentHeader from '../../components/shared/ContentHeader'
import styles from '../../styles/sales.module.scss'
import { BANNER_CLICK_TYPE } from '../../utils/banner'

const BannerListPage = () => {
  const [bannerList, setBannerList] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [totalElements, setTotalElements] = useState(0)
  const [isShow, setIsShow] = useState('')
  const [bannerPosition, setBannerPosition] = useState('') // 기본값 예시
  const [previewImage, setPreviewImage] = useState(null)
  const [previewVisible, setPreviewVisible] = useState(false)
  const navigate = useNavigate()
  const [bannerCategoryList, setBannerCategoryList] = useState([])

  useEffect(() => {
    fetchBannerList(page, pageSize, bannerPosition, isShow)
    // eslint-disable-next-line
  }, [page, pageSize, bannerPosition, isShow])

  useEffect(() => {
    fetchBannerCategoryList()
    // eslint-disable-next-line
  }, [])

  const fetchBannerClickCount = async (bannerId, bannerPosition) => {
    const params = {
      bannerId: bannerId,
      bannerPosition: bannerPosition,
    }
    const res = await getBannerClickCount(params)
    console.log('res', res)
  }

  const fetchBannerCategoryList = async () => {
    const res = await getBannerCategoryList()
    setBannerCategoryList(Array.isArray(res.data) ? res.data : [])
  }

  const fetchBannerList = async (
    page = 0,
    size = 10,
    bannerPosition = null,
    isShow = null,
  ) => {
    setIsLoading(true)
    const params = {
      page,
      size,
      bannerPosition,
      isShow,
    }
    try {
      const res = await getBannerList(params)
      const banners = res.data.content
      // 각 배너별로 clickCount를 병렬로 받아오기
      const bannersWithClickCount = await Promise.all(
        banners.map(async (banner) => {
          try {
            const clickRes = await getBannerClickCount({
              bannerId: banner.id,
              bannerPosition: banner.position,
            })
            return {
              ...banner,
              clickCount: clickRes.data?.toLocaleString() || 0,
            }
          } catch (e) {
            return { ...banner, clickCount: 0 }
          }
        }),
      )
      setBannerList(bannersWithClickCount)
      setTotalElements(res.data.totalElements)
    } catch (err) {
      console.log('getBannerList err', err)
    } finally {
      setIsLoading(false)
    }
  }

  // 카테고리 key->label 매핑
  const categoryKeyToLabel = (key) => {
    const found = bannerCategoryList.find((cat) => cat.key === key)
    return found ? found.label : key
  }

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 50,
      align: 'center',
    },
    {
      title: '노출위치',
      dataIndex: 'position',
      key: 'position',
      width: 120,
      align: 'center',
      render: (position) => categoryKeyToLabel(position) || '-',
    },
    {
      title: '정렬\n순서',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 80,
      align: 'center',
    },
    {
      title: '콘텐츠',
      dataIndex: 'bannerImageUrl',
      key: 'bannerImageUrl',
      width: 150,
      align: 'center',
      render: (url, record) => {
        if (record.position === 'HOME_TOP_BENEFIT') {
          return (
            <span style={{ fontSize: 14 }}>
              {record.bannerImageUrl?.split(',')[0]}
            </span>
          )
        }
        return url ? (
          <img
            src={url}
            alt="banner"
            style={{
              width: 120,
              height: 40,
              objectFit: 'contain',
              cursor: 'pointer',
            }}
            onClick={(e) => {
              e.stopPropagation()
              setPreviewImage(url)
              setPreviewVisible(true)
            }}
          />
        ) : (
          '-'
        )
      },
    },
    {
      title: '이동타입',
      dataIndex: 'navigationType',
      key: 'navigationType',
      width: 120,
      align: 'center',
      render: (type) => BANNER_CLICK_TYPE[type] || '-',
    },
    {
      title: '클릭수',
      dataIndex: 'clickCount',
      key: 'clickCount',
      width: 80,
      align: 'center',
      render: (clickCount) => (
        <span style={{ fontSize: 11 }}>{clickCount}</span>
      ),
    },
    {
      title: '노출 여부',
      dataIndex: 'isShow',
      key: 'isShow',
      width: 80,
      style: { fontSize: 10 },
      align: 'center',
      render: (isShow) => (
        <span style={{ fontSize: 14 }}>{isShow ? '노출' : '미노출'}</span>
      ),
    },
    {
      title: '수정일',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 100,
      render: (date) => <span style={{ fontSize: 12 }}>{date}</span>,
      align: 'center',
    },
  ]

  const bannerPositionOptions = [
    { label: '전체(위치)', value: '' },
    ...(Array.isArray(bannerCategoryList)
      ? bannerCategoryList.map((cat) => ({ label: cat.label, value: cat.key }))
      : []),
  ]

  const isShowOptions = [
    {
      value: '',
      label: '전체(노출 여부)',
    },
    {
      value: true,
      label: (
        <>
          <EyeOutlined style={{ color: '#1677ff', marginRight: 4 }} />
          노출
        </>
      ),
    },
    {
      value: false,
      label: (
        <>
          <EyeOutlined
            style={{ color: '#aaa', marginRight: 4, transform: 'scaleX(-1)' }}
          />
          미노출
        </>
      ),
    },
  ]

  return (
    <>
      <ContentHeader title="배너 관리" />
      <Modal
        open={previewVisible}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        centered
        width={600}
      >
        {previewImage && (
          <img
            src={previewImage}
            alt="banner preview"
            style={{
              width: '100%',
              maxHeight: '70vh',
              objectFit: 'contain',
              display: 'block',
              margin: '0 auto',
            }}
          />
        )}
      </Modal>
      <div
        className={`${styles['product-register-wrap']}`}
        style={{ minWidth: '1024px' }}
      >
        {/* 상태 필터 드롭박스 등은 필요시 추가 */}
        <Flex
          justify="space-between"
          align="center"
          style={{
            background: '#f8fafd',
            borderRadius: 10,
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            padding: '18px 24px',
            marginTop: 24,
            marginBottom: 18,
            gap: 16,
          }}
        >
          <div style={{ display: 'flex', gap: 16 }}>
            <Select
              style={{ width: 220 }}
              value={bannerPosition}
              options={bannerPositionOptions.map((opt) => ({
                ...opt,
                label: (
                  <>
                    <EnvironmentOutlined
                      style={{ color: '#1677ff', marginRight: 4 }}
                    />
                    {opt.label}
                  </>
                ),
              }))}
              onChange={(value) => {
                setBannerPosition(value)
                setPage(0)
              }}
              placeholder="노출위치 선택"
              allowClear
            />
            <Select
              style={{ width: 180 }}
              value={isShow}
              options={isShowOptions}
              onChange={(value) => {
                setIsShow(value)
                setPage(0)
              }}
              placeholder="노출 여부"
              allowClear
            />
          </div>
          <span style={{ fontWeight: 600, fontSize: 18, color: '#1677ff' }}>
            총{' '}
            <span style={{ fontWeight: 'bold', fontSize: 22 }}>
              {totalElements}
            </span>
            건
          </span>
        </Flex>

        <Row
          className={`${styles['product-register-border-box']}`}
          wrap={false}
          style={{ marginTop: 20, borderBottom: 0 }}
        >
          <span className={`${styles['product-register-box-title']}`}>
            배너 목록
          </span>
        </Row>
        <div
          className={`${styles['product-register-border-box']}`}
          style={{ padding: '20px 25px 40px', height: 'fit-content' }}
        >
          <Table
            dataSource={bannerList}
            rowKey="id"
            columns={columns}
            loading={isLoading}
            pagination={{
              current: page + 1,
              pageSize: pageSize,
              total: totalElements,
              onChange: (p, ps) => {
                setPage(p - 1)
                setPageSize(ps)
              },
              showSizeChanger: true,
            }}
            bordered
            onRow={(record) => ({
              onClick: () => navigate(`/banner/update/${record.id}`),
              style: { cursor: 'pointer' },
            })}
          />
        </div>
      </div>
    </>
  )
}

export default BannerListPage
